/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Code Explorer Store
 */
import _ from 'lodash';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { DEFAULT_SELECTED_CODE_SET_SYSTEM } from 'app-constants/codeExplorer';
import { DEFAULT_PAGINATION } from '@veritable/common/constants';

const initialState: CodeExplorer.IValues = {
	searchModel: {
		searchterm: '',
		codesetsystem: DEFAULT_SELECTED_CODE_SET_SYSTEM,
	},
	searchPageNumber: DEFAULT_PAGINATION.pageNumber,
	searchPageSize: DEFAULT_PAGINATION.pageSize,
	favoritePageNumber: DEFAULT_PAGINATION.pageNumber,
	favoritePageSize: DEFAULT_PAGINATION.pageSize,
	// etag states
	modifiersETag: null,
	modifiers: null,
};

const useCodeExplorerStore = create<CodeExplorer.IStore>()(
	persist(
		devtools(
			(set) => ({
				...initialState,
				actions: {
					resetSearch: () =>
						set({
							searchModel: { searchterm: '', codesetsystem: DEFAULT_SELECTED_CODE_SET_SYSTEM },
							searchPageNumber: DEFAULT_PAGINATION.pageNumber,
							searchPageSize: DEFAULT_PAGINATION.pageSize,
							favoritePageNumber: DEFAULT_PAGINATION.pageNumber,
							favoritePageSize: DEFAULT_PAGINATION.pageSize,
						}),
					resetSearchPagination: () =>
						set({ searchPageNumber: DEFAULT_PAGINATION.pageNumber, searchPageSize: DEFAULT_PAGINATION.pageSize }),
					setSearchModel: (searchModel) => set({ searchModel }),
					setSearchPageNumber: (searchPageNumber) => set({ searchPageNumber }),
					setSearchPageSize: (searchPageSize) => set({ searchPageSize }),
					setFavoritePageNumber: (favoritePageNumber) => set({ favoritePageNumber }),
					setFavoritePageSize: (favoritePageSize) => set({ favoritePageSize }),
					setModifiers: (modifiers, modifiersETag) => set({ modifiers, modifiersETag }),
					clearModifiers: () => set({ modifiers: null, modifiersETag: null }),
				},
			}),
			{ name: 'Code Explorer' },
		),
		{
			name: 'code-explorer',
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({
				searchModel: state.searchModel,
				modifiers: state.modifiers,
				modifiersETag: state.modifiersETag,
			}),
		},
	),
);

const useCodeExplorerStoreValues = (): CodeExplorer.IValues => useCodeExplorerStore(useShallow((state) => _.omit(state, ['actions'])));
const useCodeExplorerStoreActions = (): CodeExplorer.IActions => useCodeExplorerStore(useShallow((state) => state.actions));

export { DEFAULT_PAGINATION as defaultPagination, useCodeExplorerStoreValues, useCodeExplorerStoreActions };
export default useCodeExplorerStore;
